import { useCallback, useEffect, useState } from 'react';

interface PlantingGroupTableFilters {
  agronomist: string[];
  setAgronomist: (value: string[]) => void;
  subDistrict: string[];
  setSubDistrict: (value: string[]) => void;
  groupType?: string;
  setGroupType: (value?: string) => void;
  seasonStatus?: string;
  setSeasonStatus: (value?: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  plantingGroupName: string;
  setPlantingGroupName: (name: string) => void;
  plantingGroupSearchTerm: string;
  setPlantingGroupSearchTerm: (term: string) => void;
  onResetFilters: () => void;
  onPageChange: (page: number, pageSize: number) => void;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

const usePlantingGroupTableFilters = (): PlantingGroupTableFilters => {
  // Filter states
  const [agronomist, setAgronomist] = useState<string[]>([]);
  const [subDistrict, setSubDistrict] = useState<string[]>([]);
  const [groupType, setGroupType] = useState<string>();
  const [seasonStatus, setSeasonStatus] = useState<string>();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Search states
  const [plantingGroupName, setPlantingGroupName] = useState<string>('');
  const [plantingGroupSearchTerm, setPlantingGroupSearchTerm] =
    useState<string>('');

  // Event handlers
  const handlePageChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const clearPlantingGroupTableFilters = useCallback(() => {
    setAgronomist([]);
    setSubDistrict([]);
    setGroupType(undefined);
    setSeasonStatus(undefined);
    setCurrentPage(DEFAULT_PAGE);
    setPageSize(DEFAULT_PAGE_SIZE);
    setPlantingGroupName('');
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(DEFAULT_PAGE);
  }, [agronomist, subDistrict, seasonStatus, groupType, plantingGroupName]);

  return {
    agronomist,
    setAgronomist,
    subDistrict,
    setSubDistrict,
    groupType,
    setGroupType,
    seasonStatus,
    setSeasonStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    plantingGroupName,
    setPlantingGroupName,
    plantingGroupSearchTerm,
    setPlantingGroupSearchTerm,
    onResetFilters: clearPlantingGroupTableFilters,
    onPageChange: handlePageChange,
  };
};

export default usePlantingGroupTableFilters;
